import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import { Box, Button, Drawer, GlobalStyles, LinearProgress, Typography, useTheme } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { compact } from 'es-toolkit';
import { first, isArray, isEmpty, map, orderBy } from 'es-toolkit/compat';
import { AgentSession } from 'interfaces/agentSession';
import { Permission } from 'interfaces/permissionOption';
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { isReportCanvasRoutePath } from 'components/Pages/StudyReport/studyReportRouteNavigation';
import { useLabs } from 'utils/queryHooks/useLabs';
import { useStudies } from 'utils/queryHooks/useStudies';
import { useCurrentLabId } from 'utils/useCurrentLab';
import { useCurrentUser } from 'utils/useCurrentUser';
import { useEncodedFilters } from 'utils/useEncodedFilters';
import { usePermissions } from 'utils/usePermissions';
import useSessionTransitionLoading from 'utils/useSessionTransitionLoading';
import { useCopilotDrawerState } from '../hooks/useCopilotDrawerState';
import { CopilotBackgroundTasksTab } from './CopilotBackgroundTasksTab';
import { CopilotChatTab } from './CopilotChatTab';
import { CopilotDrawerHeader, CopilotDrawerTabValue } from './CopilotDrawerHeader';
import { CopilotPromptComposer } from './CopilotPromptComposer';
import { CopilotSessionsTab } from './CopilotSessionsTab';
import { normalizeOptionText } from './helpers';
import { useCopilotChatFlow } from './hooks/useCopilotChatFlow';
import { useCopilotDrawerResizer } from './hooks/useCopilotDrawerResizer';
import { useCopilotSessionManagement } from './hooks/useCopilotSessionManagement';
import { mergeVisibleActiveSessions } from './sessionListState';

// Orchestrates the drawer layout and wires specialized hooks that own
// chat flow, session management, and resize behavior.
export const CopilotDrawer: React.FC<React.PropsWithChildren<unknown>> = () => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const isWorkspaceOpen = isReportCanvasRoutePath(location.pathname);
  const { hasPermission, isLoading: permissionsLoading } = usePermissions();
  const canViewNucleaiCopilot =
    hasPermission(Permission.AccessAnalysisStudio) && hasPermission(Permission.ViewNucleaiCopilot);
  const canViewCopilotTokenUsage = hasPermission(Permission.ViewCopilotTokenUsage);
  const canViewMultipleLabs = hasPermission(Permission.ApplyPermissionsAcrossLabs);
  const {
    copilotDrawerOpen,
    copilotDrawerTab,
    copilotDrawerWidth,
    setCopilotDrawerActivityCount,
    copilotDrawerSessionId,
    setCopilotDrawerTab,
    setCopilotDrawerOpen,
    setCopilotDrawerSessionId,
    setCopilotDrawerWidth,
  } = useCopilotDrawerState();
  const currentUser = useCurrentUser();
  const { labId, setLabId } = useCurrentLabId();
  const { queryParams, setQueryParams } = useEncodedFilters();
  const { data: studies, isLoading: studiesLoading } = useStudies(labId);
  const studyOptions = React.useMemo(
    () => (studies && studies.length > 0 ? map(studies, (study) => ({ value: study.id, text: study.name })) : null),
    [studies]
  );
  const { data: labs, isLoading: labsLoading } = useLabs();
  const labOptions = React.useMemo(
    () =>
      orderBy(
        map(labs, (lab) => ({ value: lab.id, text: normalizeOptionText(lab.name) })),
        [(option) => normalizeOptionText(option.text).toLowerCase()]
      ),
    [labs]
  );
  const rawStudyId = queryParams.filters?.studyId;
  // Keep study selection aligned with filter state so all tabs share context.
  const selectedStudyIds = compact(isArray(rawStudyId) ? rawStudyId : rawStudyId ? [rawStudyId] : []);
  const selectedStudyId = first(selectedStudyIds);
  // Route param takes precedence, local fallback is used for newly-created sessions.
  const routeSessionId = React.useMemo(() => new URLSearchParams(location.search).get('sessionId'), [location.search]);

  const [promptDraft, setPromptDraft] = React.useState('');
  const activeTab: CopilotDrawerTabValue = copilotDrawerTab;

  React.useLayoutEffect(() => {
    if (!routeSessionId) {
      return;
    }

    // Route-driven session selection must survive page-level remounts when the
    // user closes the report canvas and continues elsewhere in the app.
    setCopilotDrawerSessionId(routeSessionId);
  }, [routeSessionId, setCopilotDrawerSessionId]);

  const activeSessionId = routeSessionId || copilotDrawerSessionId;

  React.useEffect(() => {
    if (isWorkspaceOpen || !copilotDrawerOpen || routeSessionId || !activeSessionId) {
      return;
    }

    const searchParams = new URLSearchParams(location.search);
    searchParams.set('sessionId', activeSessionId);
    const nextSearch = searchParams.toString();
    const currentSearch = location.search.startsWith('?') ? location.search.slice(1) : location.search;

    if (nextSearch === currentSearch) {
      return;
    }

    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : '',
      },
      { replace: true, state: location.state }
    );
  }, [
    activeSessionId,
    copilotDrawerOpen,
    isWorkspaceOpen,
    location.pathname,
    location.search,
    location.state,
    navigate,
    routeSessionId,
  ]);

  // UI-only resize interactions and document-level side effects (cursor/select).
  const {
    isResizingDrawer,
    minCopilotDrawerWidth,
    maxCopilotDrawerWidth,
    handleResizePointerDown,
    handleResizeHandleKeyDown,
  } = useCopilotDrawerResizer({
    copilotDrawerWidth,
    setCopilotDrawerWidth,
  });

  // Session queries + archive/unarchive optimistic updates are isolated here.
  const {
    sessionsQuery,
    archivedSessionsQuery,
    activeSessions,
    archivedSessions,
    activeSessionQuery,
    archiveMutationSessionIds,
    selectionLoadingSessionId,
    suggestedSession,
    handleSelectSession,
    handleArchiveSession,
    handleUnarchiveSession,
  } = useCopilotSessionManagement({
    activeSessionId,
    copilotDrawerOpen,
    setFallbackSessionId: setCopilotDrawerSessionId,
    setActiveTab: setCopilotDrawerTab,
  });

  const isActiveSessionTransitionLoading = useSessionTransitionLoading({
    sessionId: activeSessionId,
    resolvedSessionId: activeSessionQuery.data?.id ?? null,
    isFetching: Boolean(activeSessionQuery.isLoading || activeSessionQuery.isFetching),
    isError: activeSessionQuery.isError,
    enabled: Boolean(activeSessionId && copilotDrawerOpen),
  });

  const activeSessionScopeMismatch = React.useMemo(() => {
    const activeSession = activeSessionQuery.data;

    if (!activeSessionId || !activeSession) {
      return null;
    }

    const sessionLabId = activeSession.sessionData?.reportState?.filters?.labId || null;
    const sessionStudyIds = [
      ...compact(activeSession.sessionData?.reportState?.studyIds || activeSession.studyIds || []),
    ].sort();
    const currentStudyIds = [...compact(selectedStudyIds)].sort();
    const hasLabMismatch = Boolean(labId && sessionLabId && labId !== sessionLabId);
    const hasStudyMismatch =
      currentStudyIds.length > 0 &&
      sessionStudyIds.length > 0 &&
      currentStudyIds.join('|') !== sessionStudyIds.join('|');

    if (!hasLabMismatch && !hasStudyMismatch) {
      return null;
    }

    return {
      currentLabId: labId,
      currentStudyIds: selectedStudyIds,
      sessionLabId,
      sessionStudyIds,
    };
  }, [activeSessionId, activeSessionQuery.data, labId, selectedStudyIds]);

  // Message send/stream/retry/autosubmit lifecycle is isolated here.
  const {
    messages,
    promptSendState,
    isStreaming,
    agentStatus,
    agentProgress,
    agentActions,
    tokenTraces,
    sessionActivityById,
    backgroundSessionActivityCount,
    hiddenSessionActivityCount,
    isChatLoading,
    clearSessionViewState,
    stopStreamingSession,
    handleSendPrompt,
    handleRetry,
    handleUsePromptSuggestion,
    handleSendPromptSuggestion,
    handleOpenWorkspace,
    handleStartNewChat,
    handleStop,
  } = useCopilotChatFlow({
    state: {
      promptDraft,
    },
    setters: {
      setPromptDraft,
    },
    sessionContext: {
      activeSessionId,
      activeSession: activeSessionQuery.data,
      activeSessionIsTransitionLoading: isActiveSessionTransitionLoading,
      copilotDrawerOpen,
      selectedStudyIds,
      selectedStudyId,
      labId,
      knownSessions: activeSessions,
      currentUserId: currentUser?.userId,
      setFallbackSessionId: setCopilotDrawerSessionId,
    },
  });

  const handleArchiveSessionWithLiveCleanup = React.useCallback(
    async (session: Parameters<typeof handleArchiveSession>[0]) => {
      stopStreamingSession(session.id, { restorePrompt: false });
      clearSessionViewState(session.id);
      await handleArchiveSession(session);
    },
    [clearSessionViewState, handleArchiveSession, stopStreamingSession]
  );

  const getCachedSessionById = React.useCallback(
    (sessionId: string) => queryClient.getQueryData<AgentSession>(['agentSession', sessionId]),
    [queryClient]
  );

  const visibleActiveSessions = React.useMemo(
    () =>
      mergeVisibleActiveSessions({
        activeSessions,
        activeSession: activeSessionQuery.data,
        activeSessionId,
        sessionActivityById,
        getSessionById: getCachedSessionById,
      }),
    [activeSessionId, activeSessionQuery.data, activeSessions, getCachedSessionById, sessionActivityById]
  );

  const handleCloseDrawer = React.useCallback(() => {
    setCopilotDrawerOpen(false, 'replaceIn');
  }, [setCopilotDrawerOpen]);

  const handleResumeSuggestedSession = React.useCallback(() => {
    if (suggestedSession) {
      handleSelectSession(suggestedSession);
    }
  }, [handleSelectSession, suggestedSession]);

  React.useEffect(() => {
    setCopilotDrawerActivityCount(hiddenSessionActivityCount);
  }, [hiddenSessionActivityCount, setCopilotDrawerActivityCount]);

  React.useEffect(() => {
    return () => {
      // Route transitions remount the drawer between shells; active streams must survive that handoff.
      setCopilotDrawerActivityCount(0);
    };
  }, [setCopilotDrawerActivityCount]);

  const handleSwitchToSessionContext = React.useCallback(() => {
    if (!activeSessionScopeMismatch) {
      return;
    }

    const { sessionLabId, sessionStudyIds } = activeSessionScopeMismatch;

    if (sessionLabId && sessionLabId !== labId) {
      setLabId(sessionLabId, 'replaceIn');
    }

    if (sessionStudyIds.length > 0) {
      setQueryParams(
        {
          ...(sessionLabId ? { labId: sessionLabId } : {}),
          filters: {
            studyId: sessionStudyIds.length === 1 ? sessionStudyIds[0] : sessionStudyIds,
          },
          page: 1,
        },
        'replaceIn'
      );
    }
  }, [activeSessionScopeMismatch, labId, setLabId, setQueryParams]);

  const shouldKeepDrawerMountedDuringLoad = copilotDrawerOpen && permissionsLoading;
  const isLabContextLoading = permissionsLoading || labsLoading || studiesLoading;
  const shouldBlockDrawerInteractions = permissionsLoading;
  const isSessionSelectionLoading = Boolean(selectionLoadingSessionId);
  const isChatPanelLoading = isChatLoading || isSessionSelectionLoading;
  const isReadOnlyScopeMismatch = Boolean(!isActiveSessionTransitionLoading && activeSessionScopeMismatch);

  if (!canViewNucleaiCopilot && !shouldKeepDrawerMountedDuringLoad) {
    return null;
  }

  // Lab switch clears study filter to prevent cross-lab stale study IDs.
  const handleLabChange = (newLabId: string) => {
    setLabId(newLabId, 'replaceIn');
    setQueryParams({ labId: newLabId, filters: { ...queryParams.filters, studyId: undefined }, page: 1 }, 'replaceIn');
  };

  const handleStudyIdsChange = (studyIds: string[]) => {
    setQueryParams(
      {
        filters: {
          ...queryParams.filters,
          studyId: isEmpty(studyIds) ? undefined : studyIds.length === 1 ? studyIds[0] : studyIds,
        },
        page: 1,
      },
      'replaceIn'
    );
  };

  return (
    <>
      <GlobalStyles
        styles={{
          '#root': {
            paddingRight: copilotDrawerOpen ? `${copilotDrawerWidth}px` : 0,
            transition: `padding-right var(--copilot-drawer-layout-transition-duration, ${theme.transitions.duration.enteringScreen}ms) ${theme.transitions.easing.easeOut}`,
          },
        }}
      />
      <Drawer
        anchor="right"
        open={copilotDrawerOpen}
        onClose={handleCloseDrawer}
        variant="persistent"
        ModalProps={{ keepMounted: false }}
        sx={{
          width: copilotDrawerWidth,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: {
            width: copilotDrawerWidth,
            zIndex: theme.zIndex.appBar + 1,
            height: '100%',
            top: 0,
            borderLeft: 1,
            borderColor: 'divider',
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box
            component="button"
            type="button"
            role="separator"
            aria-label="Resize copilot drawer"
            aria-orientation="vertical"
            aria-valuemin={minCopilotDrawerWidth}
            aria-valuemax={maxCopilotDrawerWidth}
            aria-valuenow={copilotDrawerWidth}
            onPointerDown={handleResizePointerDown}
            onKeyDown={handleResizeHandleKeyDown}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              bottom: 0,
              width: 14,
              p: 0,
              m: 0,
              border: 0,
              bgcolor: 'transparent',
              cursor: 'col-resize',
              touchAction: 'none',
              zIndex: 2,
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: 6,
                width: 2,
                borderRadius: 999,
                bgcolor: isResizingDrawer ? 'primary.main' : 'transparent',
                transition: theme.transitions.create(['background-color'], {
                  duration: theme.transitions.duration.shortest,
                }),
              },
              '&:hover::before, &:focus-visible::before': {
                bgcolor: 'primary.main',
              },
            }}
          />
          <CopilotDrawerHeader
            activeTab={activeTab}
            onTabChange={(value) => setCopilotDrawerTab(value, 'replaceIn')}
            onClose={handleCloseDrawer}
            sessionActivityCount={backgroundSessionActivityCount}
          />

          {isLabContextLoading && (
            <Box sx={{ px: 2, pb: 1.5 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: 'block',
                  mb: 0.75,
                  fontWeight: 700,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                }}
              >
                Updating lab context
              </Typography>
              <LinearProgress sx={{ height: 4, borderRadius: 999 }} />
            </Box>
          )}

          <Box
            sx={{
              px: 2,
              pt: activeTab === 'chat' ? 0 : 2,
              pb: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              flex: '1 1 auto',
              overflowY: 'auto',
              opacity: isLabContextLoading ? 0.72 : 1,
              pointerEvents: shouldBlockDrawerInteractions ? 'none' : 'auto',
              transition: theme.transitions.create(['opacity'], {
                duration: theme.transitions.duration.shortest,
              }),
            }}
          >
            {activeTab === 'chat' && (
              <CopilotChatTab
                activeSessionId={activeSessionId}
                canViewMultipleLabs={canViewMultipleLabs}
                canViewTokenUsageDebug={canViewCopilotTokenUsage}
                labOptions={labOptions}
                labId={labId}
                controlsDisabled={isLabContextLoading}
                onLabChange={handleLabChange}
                studyOptions={studyOptions}
                selectedStudyIds={selectedStudyIds}
                onStudyIdsChange={handleStudyIdsChange}
                onStartNewChat={handleStartNewChat}
                isChatLoading={isChatPanelLoading}
                messages={isChatPanelLoading ? [] : messages}
                isStreaming={isStreaming}
                isWorkspaceOpen={isWorkspaceOpen}
                onRetry={handleRetry}
                agentStatus={agentStatus}
                agentProgress={agentProgress}
                agentActions={agentActions}
                tokenTraces={tokenTraces}
                sessionTitle={isChatPanelLoading ? undefined : activeSessionQuery.data?.title}
                suggestedSessionTitle={suggestedSession?.title}
                onResumeSuggestedSession={handleResumeSuggestedSession}
                scopeMismatch={isChatPanelLoading ? null : activeSessionScopeMismatch}
                sessionStudyNames={isChatPanelLoading ? null : activeSessionQuery.data?.studyNames}
                onStartNewChatForCurrentContext={handleStartNewChat}
                onSwitchToSessionContext={handleSwitchToSessionContext}
                onUsePromptSuggestion={handleUsePromptSuggestion}
                onSendPromptSuggestion={handleSendPromptSuggestion}
                promptSuggestionsDisabled={
                  isLabContextLoading ||
                  isReadOnlyScopeMismatch ||
                  isSessionSelectionLoading ||
                  promptSendState !== 'idle'
                }
              />
            )}

            {activeTab === 'sessions' && (
              <CopilotSessionsTab
                activeSessions={visibleActiveSessions}
                archivedSessions={archivedSessions}
                activeSessionId={activeSessionId}
                archiveMutationSessionIds={archiveMutationSessionIds}
                sessionActivityById={sessionActivityById}
                isLoading={sessionsQuery.isLoading}
                isArchivedLoading={archivedSessionsQuery.isLoading}
                studyOptions={studyOptions}
                labs={labOptions}
                onSelectSession={handleSelectSession}
                onArchiveSession={handleArchiveSessionWithLiveCleanup}
                onUnarchiveSession={handleUnarchiveSession}
              />
            )}

            {activeTab === 'tasks' && <CopilotBackgroundTasksTab selectedStudyId={selectedStudyId} />}
          </Box>

          {activeTab === 'chat' && (
            <Box sx={{ backgroundColor: 'background.paper' }}>
              <CopilotPromptComposer
                promptDraft={promptDraft}
                setPromptDraft={setPromptDraft}
                onSendPrompt={handleSendPrompt}
                onStop={handleStop}
                sendState={promptSendState}
                disabled={isLabContextLoading || isReadOnlyScopeMismatch || isSessionSelectionLoading}
              />
              {!isWorkspaceOpen && (
                <Box sx={{ px: 2, pb: 2, pt: 0, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<ArrowBackRoundedIcon fontSize="small" />}
                    onClick={handleOpenWorkspace}
                    disabled={isLabContextLoading || isSessionSelectionLoading}
                  >
                    Open Report Canvas
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Drawer>
    </>
  );
};
